import * as React from "react";
import { debounce } from "lodash";
import { SocialCoachSessionService, UsersService } from "@/services";
import * as DTO from "../../../../interfaces";
import { ExportCoachObject, ReportType } from "@/interfaces";
import { exportCoachesList, exportCoachesToCsv, URLS } from "@/utils";
import { selectOptions } from "./filters";
import { CoachCell } from "./CoachCell";
import { NewCoachModal } from "./NewCoachModal";
import { Button, Input, Table, Pagination, Combobox, Spinner } from "@/components/ui";
import { Search } from "lucide-react";

const searchAccountPage = (
  pageNumber: number = 0,
  searchTerm: string = "",
  coachStatuses?: DTO.CoachStatuses[],
  size: number = 20,
  hideDemoAccounts: boolean = false
) =>
  UsersService.searchCoaches(
    {
      searchTerm,
      coachStatuses,
      hideDemoAccounts,
    },
    pageNumber,
    size
  );

const CoachListFC: React.FC = () => {
  const [coaches, setCoaches] = React.useState<DTO.CompleteAccount[]>([]);
  const [coachesStatsById, setCoachesStatsById] = React.useState<{ [key: string]: DTO.CoachStatistic }>({});
  const [totalPages, setTotalPages] = React.useState<number>(0);
  const [pageNumber, setPageNumber] = React.useState<number | undefined>();
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [exportLoading, setExportLoading] = React.useState<boolean>(false);
  const [userQuery, setUserQuery] = React.useState("");
  const [showNewCoachModal, setShowNewCoachModal] = React.useState<boolean>(false);

  const [statuses, setStatuses] = React.useState<DTO.CoachStatuses[]>([
    DTO.CoachStatuses.PENDING_FOR_APPROVAL,
    DTO.CoachStatuses.ACTIVE,
  ]);

  const coachesIds = coaches.map(account => {
    return account.coach?.id;
  }) as string[];

  const getTableData = async () => {
    setIsLoading(true);
    const coachStatus = statuses.length > 0 ? statuses : undefined;
    const accountsPage = await searchAccountPage(pageNumber, searchTerm, coachStatus, 20);
    setCoaches(accountsPage.content);
    setTotalPages(accountsPage.totalPages!);
    setIsLoading(false);
  };

  const exportCoaches = async () => {
    setExportLoading(true);
    let page = 0;
    let last = false;
    let totalResult: any[] = [];
    const coachStatus = statuses.length > 0 ? statuses : undefined;
    while (!last) {
      const response = await searchAccountPage(page, searchTerm, coachStatus, 200);
      last = response.last || false;
      totalResult = totalResult.concat(response.content);
      page++;
    }

    const data: ExportCoachObject[] = exportCoachesList(totalResult);
    exportCoachesToCsv("Admin dashboard", data);
    setExportLoading(false);
  };

  const getStatistics = async () => {
    if (coachesIds.length > 0) {
      const statistics = await UsersService.searchCoachesStatistics(coachesIds, [ReportType.FEATURE_ENGAGEMENT_REPORT]);
      const statisticsById: { [key: string]: DTO.CoachStatistic } = {};
      statistics.forEach(coachStat => {
        statisticsById[coachStat.coachId] = coachStat;
      });

      setCoachesStatsById(statisticsById);
    }
  };

  const impersonateCoach = async (username: string) => {
    // Set coach auth cookie
    await SocialCoachSessionService.impersonate(username);
    // Redirect to coach dashboard
    window.location.assign(URLS.coach.dashboard);
  };

  React.useEffect(() => {
    getTableData();
  }, [pageNumber, searchTerm, statuses]);

  const sendQuery = (query: string) => {
    setSearchTerm(query);
  };

  const delayedQuery = React.useRef(debounce((q: string) => sendQuery(q), 500)).current;

  const onChange = (e: any) => {
    setUserQuery(e.target.value);
    delayedQuery(e.target.value);
  };

  React.useEffect(() => {
    getStatistics();
  }, [coaches]);

  return (
    <div className="w-full flex flex-col gap-5 p-1">
      {/* Top Filter & Actions Header */}
      <div className="flex flex-col md:flex-row items-center gap-4 w-full">
        {/* Search Bar */}
        <div className="relative w-full md:w-80 shrink-0">
          {isLoading ? (
            <Spinner className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground animate-spin" />
          ) : (
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          )}
          <Input
            data-elm-id="admin-dashboard-text-search"
            value={userQuery}
            placeholder="Search coaches..."
            onChange={onChange}
            className="pl-9 h-10 w-full text-xs font-semibold"
          />
        </div>

        {/* Multi-Status Filter Combobox */}
        <div className="w-full md:w-80 shrink-0">
          <Combobox<(typeof selectOptions)[number], true>
            multiple={true}
            placeholder="Select Statuses..."
            items={selectOptions}
            value={statuses.map(s => selectOptions.find(o => o.value === s)!)}
            onValueChange={selectedItems => {
              const selectedValues = Array.isArray(selectedItems) ? selectedItems.map(item => item.value) : [];
              setStatuses(selectedValues);
            }}
            itemToStringLabel={item => item.text}
            isItemEqualToValue={(a, b) => a?.value === b?.value}
            className="bg-white"
          />
        </div>

        <div className="grow" />

        {/* Action Buttons */}
        <div className="flex items-center gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            disabled={exportLoading}
            onClick={exportCoaches}
            className="h-10 font-bold border-gray-200 cursor-pointer"
          >
            {exportLoading && <Spinner className="mr-2 size-3.5" />}
            Export List
          </Button>
          <Button onClick={() => setShowNewCoachModal(true)} className="h-10 font-bold cursor-pointer">
            New Coach
          </Button>
        </div>
      </div>

      {/* Main Table View */}
      <div className="w-full border border-slate-200/60 rounded-xl overflow-hidden bg-background shadow-xs">
        <Table data-elm-id="admin-dashboard-coaches-table" className="w-full text-sm">
          <Table.Header className="bg-slate-50/50 border-b border-slate-100">
            <Table.Row className="hover:bg-transparent">
              <Table.Head rowSpan={2} className="font-bold text-slate-500 uppercase tracking-wider text-[11px] py-3">
                Coach Info
              </Table.Head>
              <Table.Head rowSpan={2} className="font-bold text-slate-500 uppercase tracking-wider text-[11px] py-3">
                Status
              </Table.Head>
              <Table.Head rowSpan={2} className="font-bold text-slate-500 uppercase tracking-wider text-[11px] py-3">
                Last Login
              </Table.Head>
              <Table.Head
                colSpan={4}
                className="font-bold text-center border-l border-slate-100 py-1.5 text-slate-500 uppercase tracking-wider text-[11px] bg-slate-50/25"
              >
                Players
              </Table.Head>
              <Table.Head
                colSpan={3}
                className="font-bold text-center border-l border-slate-100 py-1.5 text-slate-500 uppercase tracking-wider text-[11px] bg-slate-50/25"
              >
                Prompts
              </Table.Head>
              <Table.Head
                colSpan={3}
                className="font-bold text-center border-l border-slate-100 py-1.5 text-slate-500 uppercase tracking-wider text-[11px] bg-slate-50/25"
              >
                Feature Use (Last 90d)
              </Table.Head>
              <Table.Head
                rowSpan={2}
                className="font-bold text-right p-2 border-l border-slate-100 text-slate-500 uppercase tracking-wider text-[11px]"
              >
                Actions
              </Table.Head>
            </Table.Row>
            <Table.Row className="hover:bg-transparent border-t border-slate-100">
              {/* player stats */}
              <Table.Head className="font-semibold text-center border-l border-slate-100 text-slate-400 text-[10px] uppercase tracking-wider py-1.5 bg-slate-50/10">
                Total
              </Table.Head>
              <Table.Head className="font-semibold text-center text-slate-400 text-[10px] uppercase tracking-wider py-1.5 bg-slate-50/10">
                Active
              </Table.Head>
              <Table.Head className="font-semibold text-center text-slate-400 text-[10px] uppercase tracking-wider py-1.5 bg-slate-50/10">
                Enrolled
              </Table.Head>
              <Table.Head className="font-semibold text-center text-slate-400 text-[10px] uppercase tracking-wider py-1.5 bg-slate-50/10">
                SC+
              </Table.Head>
              {/* prompts */}
              <Table.Head className="font-semibold text-center border-l border-slate-100 text-slate-400 text-[10px] uppercase tracking-wider py-1.5 bg-slate-50/10">
                Total
              </Table.Head>
              <Table.Head className="font-semibold text-center text-slate-400 text-[10px] uppercase tracking-wider py-1.5 bg-slate-50/10">
                New
              </Table.Head>
              <Table.Head className="font-semibold text-center text-slate-400 text-[10px] uppercase tracking-wider py-1.5 bg-slate-50/10">
                Remaining
              </Table.Head>
              {/* feature activity */}
              <Table.Head className="font-semibold text-center border-l border-slate-100 text-slate-400 text-[10px] uppercase tracking-wider py-1.5 bg-slate-50/10">
                Posts
              </Table.Head>
              <Table.Head className="font-semibold text-center text-slate-400 text-[10px] uppercase tracking-wider py-1.5 bg-slate-50/10">
                Projects
              </Table.Head>
              <Table.Head className="font-semibold text-center text-slate-400 text-[10px] uppercase tracking-wider py-1.5 bg-slate-50/10">
                MVP
              </Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading && coaches.length === 0 ? (
              <Table.Row className="hover:bg-transparent">
                <Table.Cell colSpan={13} className="py-20 text-center">
                  <Spinner className="size-8" />
                </Table.Cell>
              </Table.Row>
            ) : coaches.length === 0 ? (
              <Table.Row className="hover:bg-transparent">
                <Table.Cell colSpan={13} className="py-20 text-center text-muted-foreground font-semibold text-sm">
                  No coaches found matching your filters.
                </Table.Cell>
              </Table.Row>
            ) : (
              coaches
                .filter(coachAccount => {
                  const groupStrings = (coachAccount.user?.groups || []).map(g => g.toString());
                  const isAdminOrEditor = groupStrings.includes("ADMIN") || groupStrings.includes("EDITOR");
                  return !isAdminOrEditor;
                })
                .map((coachAccount, index) => {
                  const { coach } = coachAccount;
                  return (
                    <CoachCell
                      key={"coach_cell_" + index}
                      coachIndex={index}
                      coachAccount={coachAccount}
                      coachStats={coachesStatsById[coach!.id]}
                      handlerRefresh={getTableData}
                      handlerImpersonate={impersonateCoach}
                    />
                  );
                })
            )}
          </Table.Body>
        </Table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center py-4">
          <Pagination
            data-elm-id="admin-dashboard-paginator"
            page={(pageNumber ?? 0) + 1}
            totalPages={totalPages}
            boundaryRange={3}
            onPageChange={page => {
              setPageNumber(page - 1);
            }}
          />
        </div>
      )}

      {/* New Coach Modal Trigger */}
      <NewCoachModal
        open={showNewCoachModal}
        onClose={() => setShowNewCoachModal(false)}
        onNew={() => {
          setShowNewCoachModal(false);
          getTableData();
        }}
      />
    </div>
  );
};

export const CoachList = CoachListFC;
